import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { Suspense } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { DataTable } from '@/components/ui/data-table';
import { allergens } from '@/lib/consts';
import { IngredientAllergen } from '@/types';

import { useIngredientAllergens } from '../api/ingredients-queries';

const columnHelper = createColumnHelper<IngredientAllergen>();

const columns = [
  columnHelper.accessor('created_at', {
    header: 'Date',
    cell: (info) => format(new Date(info.getValue()), 'MMM do, yyyy'),
    size: 30,
  }),
  columnHelper.accessor(
    (ingredientAllergen) => {
      const allergenName =
        allergens[ingredientAllergen.allergen_id]?.label.toLowerCase();
      // TODO: Improve this to
      // a. Merge updates that occured at the same time
      // b. Mark if the allergen was removed from allergens or may contains
      switch (ingredientAllergen.status) {
        case 'present':
          return `Added ${allergenName} as allergen`;
        case 'may_be_present':
          return `Added ${allergenName} as may be present allergen`;
        case 'not_present':
          return `Removed ${allergenName}`;
      }
    },
    {
      id: 'description',
      header: 'Description',
      cell: (info) => info.getValue(),
      size: 70,
      enableGlobalFilter: false,
    },
  ),
];

interface IngredientAllergenHistoryTablePresentationProps {
  allergenHistory: IngredientAllergen[];
}

function IngredientAllergenHistoryTablePresentation({
  allergenHistory,
}: IngredientAllergenHistoryTablePresentationProps) {
  const table = useReactTable({
    data: allergenHistory,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="w-full">
      <DataTable table={table} containerClassName="max-h-80" />
    </div>
  );
}

interface IngredientAllergenHistoryTableProps {
  ingredientId: string;
}

function IngredientAllergenHistoryTable({
  ingredientId,
}: IngredientAllergenHistoryTableProps) {
  const { data: allergenHistory } = useIngredientAllergens(ingredientId);

  return (
    <IngredientAllergenHistoryTablePresentation
      allergenHistory={allergenHistory}
    />
  );
}

interface IngredientAllergenHistoryProps {
  ingredientId: string;
}

export function IngredientAllergenHistory({
  ingredientId,
}: IngredientAllergenHistoryProps) {
  const table = useReactTable({
    data: [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem
        value="item-1"
        className="border-2 border-solid mt-6 rounded-md border-slate-200"
      >
        <AccordionTrigger className="bg-slate-200 px-4">
          Allergen History
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-4 text-balance px-4 pt-4">
          <Suspense
            fallback={
              <DataTable
                table={table}
                containerClassName="max-h-80"
                loading
                skeletonRows={3}
              />
            }
          >
            <IngredientAllergenHistoryTable ingredientId={ingredientId} />
          </Suspense>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
